import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";

import { useBuildVersionCheck } from "@/hooks/useBuildVersionCheck";

/**
 * Tells the user their tab is running an old build, and offers a reload.
 *
 * Deliberately NOT dismissible, unlike DismissibleBanner. Running stale code is
 * the defect itself, so a prompt you can wave away reproduces the bug it exists
 * to surface — someone who dismisses it stays on old code for the rest of the
 * day. It never reloads on its own either: a half-finished invoice must not be
 * discarded to win freshness, so the reload is always an explicit click.
 */
export function UpdateAvailableBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const isStale = useBuildVersionCheck();

  if (!isStale) return null;

  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <RefreshCw
          className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden
        />
        <p className="min-w-0 font-medium">
          {t(
            "updateBanner.message",
            "Hay una versión nueva de Fiscalo. Recargá para actualizar.",
          )}
        </p>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="shrink-0 rounded-md bg-amber-600 px-3 py-1 font-medium text-white transition-colors duration-150 hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600"
      >
        {t("updateBanner.reload", "Recargar")}
      </button>
    </div>
  );
}
